import { Component } from '@opten/compiler/metadata/component';
import { OnInit } from '@opten/compiler/metadata/lifecycle';

import { createTextMaskInputElement } from 'text-mask-core/dist/textMaskCore';

const comma = `'`;
const period = '.';
const minus = '-';
const minusRegExp = /-/;
const nonDigitsRegExp = /\D+/g;
const number = 'number';
const digitRegExp = /\d/;
const caretTrap = '[]';

@Component({ className: 'js-text-mask-currency' })
export class TextMaskCurrency implements OnInit {
    constructor(private _element: HTMLInputElement) {}

    onInit() {
        const mask = createTextMaskInputElement(
            Object.assign(
                { inputElement: this._element },
                {
                    mask: createNumberMask(),
                    guide: true,
                    placeholderChar: '\u2000',
                    keepCharPositions: false
                }
            )
        );

        this._element.addEventListener('input', () =>
            mask.update(this._element.value)
        );

        mask.update(this._element.value);
    }
}

function createNumberMask({
    prefix = 'CHF ',
    suffix = '',
    includeThousandsSeparator = true,
    thousandsSeparatorSymbol = comma,
    allowDecimal = false,
    decimalSymbol = period,
    decimalLimit = 2,
    requireDecimal = false,
    allowNegative = false,
    allowLeadingZeroes = false,
    integerLimit = null
} = {}) {
    const prefixLength = (prefix && prefix.length) || 0;
    const suffixLength = (suffix && suffix.length) || 0;
    const thousandsSeparatorSymbolLength =
        (thousandsSeparatorSymbol && thousandsSeparatorSymbol.length) || 0;

    return function(rawValue = '') {
        const rawValueLength = rawValue.length;

        if (
            rawValue === '' ||
            (rawValue[0] === prefix[0] && rawValueLength === 1)
        ) {
            return prefix
                .split('')
                .concat([digitRegExp as any])
                .concat(suffix.split(''));
        } else if (rawValue === decimalSymbol && allowDecimal) {
            return prefix
                .split('')
                .concat(['0', decimalSymbol, digitRegExp as any])
                .concat(suffix.split(''));
        }

        const isNegative = rawValue[0] === minus && allowNegative;
        // if negative remove "-" sign
        if (isNegative) {
            rawValue = rawValue.toString().substr(1);
        }

        const indexOfLastDecimal = rawValue.lastIndexOf(decimalSymbol);
        const hasDecimal = indexOfLastDecimal !== -1;

        let integer;
        let fraction;
        let mask;

        // remove the suffix
        if (rawValue.slice(suffixLength * -1) === suffix) {
            rawValue = rawValue.slice(0, suffixLength * -1);
        }

        if (hasDecimal && (allowDecimal || requireDecimal)) {
            integer = rawValue.slice(
                rawValue.slice(0, prefixLength) === prefix ? prefixLength : 0,
                indexOfLastDecimal
            );

            fraction = rawValue.slice(indexOfLastDecimal + 1, rawValueLength);
            fraction = convertToMask(fraction.replace(nonDigitsRegExp, ''));
        } else {
            if (rawValue.slice(0, prefixLength) === prefix) {
                integer = rawValue.slice(prefixLength);
            } else {
                integer = rawValue;
            }
        }

        if (integerLimit && typeof integerLimit === number) {
            const thousandsSeparatorRegex =
                thousandsSeparatorSymbol === '.'
                    ? '[.]'
                    : `${thousandsSeparatorSymbol}`;
            const numberOfThousandSeparators = (
                integer.match(new RegExp(thousandsSeparatorRegex, 'g')) || []
            ).length;

            integer = integer.slice(
                0,
                integerLimit +
                    numberOfThousandSeparators * thousandsSeparatorSymbolLength
            );
        }

        integer = integer.replace(nonDigitsRegExp, '');

        if (!allowLeadingZeroes) {
            integer = integer.replace(/^0+(0$|[^0])/, '$1');
        }

        integer = includeThousandsSeparator
            ? addThousandsSeparator(integer, thousandsSeparatorSymbol)
            : integer;

        mask = convertToMask(integer);

        if ((hasDecimal && allowDecimal) || requireDecimal === true) {
            if (rawValue[indexOfLastDecimal - 1] !== decimalSymbol) {
                mask.push(caretTrap);
            }

            mask.push(decimalSymbol, caretTrap);

            if (fraction) {
                if (typeof decimalLimit === number) {
                    fraction = fraction.slice(0, decimalLimit);
                }

                mask = mask.concat(fraction);
            }

            if (
                requireDecimal === true &&
                rawValue[indexOfLastDecimal - 1] === decimalSymbol
            ) {
                mask.push(digitRegExp);
            }
        }

        if (prefixLength > 0) {
            mask = prefix.split('').concat(mask);
        }

        if (isNegative) {
            // if user is entering a negative number, add a mask placeholder spot to attract the caret to it.
            if (mask.length === prefixLength) {
                mask.push(digitRegExp);
            }

            mask = [minusRegExp].concat(mask);
        }

        if (suffix.length > 0) {
            mask = mask.concat(suffix.split(''));
        }

        return mask;
    };
}

function convertToMask(strNumber) {
    return strNumber
        .split('')
        .map(char => (digitRegExp.test(char) ? digitRegExp : char));
}

// http://stackoverflow.com/a/10899795/604296
function addThousandsSeparator(n, thousandsSeparatorSymbol) {
    return n.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparatorSymbol);
}
